/**
 * iframe 兼容工具函数
 * 处理 iframe 中的重定向和消息通信问题
 */

/**
 * 检测当前是否在 iframe 中
 */
export function isInIframe(): boolean {
    try {
        return window.self !== window.top
    } catch (e) {
        return true
    }
}

/**
 * 获取 Window 对象
 * 如果在 iframe 中，返回父窗口的 window
 * 否则返回当前窗口的 window
 */
export function getWindow(): Window {
    return isInIframe() ? window.top! : window
}

/**
 * 获取 Location 对象
 * 如果在 iframe 中，返回父窗口的 location
 * 否则返回当前窗口的 location
 */
export function getLocation(): Location {
    return getWindow().location
}

/**
 * 生成 CSRF Token
 */
export function generateCsrfToken(): string {
    return crypto.randomUUID()
}

/**
 * 向 URL 添加 query 参数（安全处理已有参数的情况）
 */
export function addQueryParam(url: string, key: string, value: string): string {
    try {
        const urlObj = new URL(url)
        urlObj.searchParams.set(key, value)
        return urlObj.href
    } catch {
        // 如果 URL 解析失败，使用简单的字符串拼接
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}${key}=${encodeURIComponent(value)}`
    }
}

/**
 * 获取重定向 URI
 * 如果在 iframe 中，返回父窗口的地址
 * 否则返回当前窗口的地址
 */
export function getRedirectUri(): string {
    return getLocation().href
}

/**
 * 获取带 CSRF Token 的重定向 URI
 */
export function getRedirectUriWithCsrfToken(): string {
    const redirectUri = getRedirectUri()
    // 留空，由后端生成
    // const csrfToken = generateCsrfToken()
    return addQueryParam(redirectUri, 'csrfToken', '')
}

/**
 * Toast 选项接口
 */
export interface ToastOptions {
    message: string
    type?: 'success' | 'info' | 'warning' | 'error'
    duration?: number
}

/**
 * patsnap-biz Toast 挂载到 document.body，z-index 由 element-plus 全局计数器分配（约 3000 起）
 * 注入 style 确保 Toast 层级高于 LoginSDK 遮罩层（99999）
 */
export function fixToastZIndex() {
    if (!document.getElementById('login-sdk-toast-zindex-fix')) {
        const style = document.createElement('style');
        style.id = 'login-sdk-toast-zindex-fix';
        style.textContent = '.patsnap-biz-toast { z-index: 100001 !important; }';
        document.head.appendChild(style);
    }
}

export function removeToastZIndex() {
    const style = document.getElementById('login-sdk-toast-zindex-fix');
    if (style) {
        style.remove();
    }
}

/**
 * 显示 Toast 提示
 * 如果在 iframe 中，通过 postMessage 发送到父窗口显示
 * 否则直接使用 patsnap-biz 的 Toast
 */
export function showToast(options: ToastOptions | string): void {
    const opts: ToastOptions = typeof options === 'string'
        ? { message: options }
        : options

    fixToastZIndex();
    if (isInIframe()) {
        // 在 iframe 中，通过 postMessage 发送到父窗口
        window.parent.postMessage(
            {
                type: 'eureka-login-toast',
                payload: opts,
                source: 'eureka-login',
            },
            '*'
        )
    } else {
        // 不在 iframe 中，直接使用 patsnap-biz 的 Toast
        // 动态导入避免在 iframe 中加载不必要的依赖
        import('patsnap-biz').then(({ Toast }) => {
            Toast(opts)
        })
    }
    setTimeout(() => {
        removeToastZIndex();
    }, opts.duration || 5000);
}
